import { useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import {
  fetchAccountSapEntries,
  fetchCompanies,
  syncAccountSapEntries,
  type Company,
  type SapEntriesResponse,
  type SapEntry,
} from "../api";
import { formatIban, formatMoney } from "../lib/grouping";

/**
 * Vista de APUNTES CONTABLES SAP para una cuenta bancaria. Los apuntes se
 * traen de SAP Business One (Service Layer) filtrando por la cuenta contable
 * (G/L) que corresponde al IBAN, resuelta automáticamente vía HouseBankAccounts.
 *
 * El botón "Sincronizar con SAP" fuerza un sync manual. En operación normal el
 * sync se dispara automáticamente tras el refresco de Wealth Reader.
 */
export function AccountSapEntries() {
  const { iban: ibanParam } = useParams<{ iban: string }>();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from ?? "/cuentas";
  const backLabel = backTo === "/resumen" ? "← Resumen" : "← Cuentas";
  const iban = ibanParam ?? "";

  const [searchParams, setSearchParams] = useSearchParams();
  const [from, setFrom] = useState(searchParams.get("from") ?? "");
  const [to, setTo] = useState(searchParams.get("to") ?? "");
  const [q, setQ] = useState(searchParams.get("q") ?? "");

  const [data, setData] = useState<SapEntriesResponse | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const load = async () => {
    if (!iban) return;
    setError(null);
    setLoading(true);
    try {
      const [e, c] = await Promise.all([
        fetchAccountSapEntries(iban, {
          from: from || undefined,
          to: to || undefined,
          q: q || undefined,
        }),
        fetchCompanies(),
      ]);
      setData(e);
      setCompanies(c.items);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iban]);

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    if (q) params.set("q", q);
    setSearchParams(params, { replace: true });
    void load();
  };

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setQ("");
    setSearchParams(new URLSearchParams(), { replace: true });
    setTimeout(() => void load(), 0);
  };

  /** Dispara el sync SAP en background y muestra un mensaje breve. No hace
   *  polling, no bloquea la vista, no recarga automáticamente — el usuario
   *  sigue navegando libremente y pulsa "Recargar" cuando quiera comprobar. */
  const doSync = async (reset = false) => {
    if (!iban || syncBusy) return;
    if (reset && !confirm("Rehacer sync completo: se borrarán los apuntes SAP locales y se bajará todo el histórico de SAP desde cero. ¿Continuar?")) return;
    setSyncBusy(true);
    setSyncMessage(null);
    try {
      const r = await syncAccountSapEntries(iban, { reset });
      setSyncMessage(
        r.started
          ? "⏳ Sync arrancado en background. Pulsa Recargar dentro de unos segundos para ver los apuntes actualizados."
          : `⚠️ ${r.message}`,
      );
    } catch (e: unknown) {
      setSyncMessage(`❌ ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSyncBusy(false);
    }
  };

  const companyLabel = (id: string) => {
    const c = companies.find((x) => x.id === id);
    return c ? (c.alias || c.name) : id || "—";
  };

  const currency = data?.account.currency || "EUR";

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Apuntes SAP · {data?.account.alias || formatIban(iban)}</h2>
          <p className="meta">
            <Link to={backTo}>{backLabel}</Link>
            {"  ·  "}
            <Link to={`/cuentas/${encodeURIComponent(iban)}/movimientos`} state={{ from: backTo }}>
              Movimientos bancarios
            </Link>
            {"  ·  "}
            <Link to={`/cuentas/${encodeURIComponent(iban)}/conciliar`} state={{ from: backTo }}>
              Conciliar
            </Link>
            {data?.account && (
              <>
                {"  ·  "}
                <code>{formatIban(data.account.iban)}</code>
                {"  ·  "}
                {data.account.bankName || data.account.bankCode}
                {"  ·  "}
                {companyLabel(data.account.companyId)}
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={() => doSync(false)}
            disabled={syncBusy}
          >
            {syncBusy ? "…" : "↻ Sincronizar con SAP"}
          </button>
          <button
            type="button"
            onClick={() => doSync(true)}
            disabled={syncBusy}
            title="Borra los apuntes locales y el cursor de sync, luego baja todo el histórico desde SAP"
          >
            ⟳ Rehacer sync completo
          </button>
          <button type="button" onClick={load} disabled={loading}>
            {loading ? "Cargando…" : "Recargar"}
          </button>
        </div>
      </div>

      {data && (
        <table className="data" style={{ marginBottom: 12 }}>
          <tbody>
            <tr>
              <th>Cuenta contable (G/L)</th>
              <td>
                {data.account.sapGLAccount ? (
                  <code>{data.account.sapGLAccount}</code>
                ) : (
                  <span className="meta">— sin descubrir aún</span>
                )}
                {data.glAccount && data.glAccount !== data.account.sapGLAccount && (
                  <>
                    {"  · en fichero: "}
                    <code>{data.glAccount}</code>
                  </>
                )}
              </td>
            </tr>
            <tr>
              <th>Cursor de sync</th>
              <td>
                {data.account.sapEntriesSyncFrom
                  ? `desde ${data.account.sapEntriesSyncFrom}`
                  : <span className="meta">— aún no se ha sincronizado</span>}
              </td>
            </tr>
            <tr>
              <th>Último sync</th>
              <td>
                {data.account.sapEntriesLastSyncedAt
                  ? new Date(data.account.sapEntriesLastSyncedAt).toLocaleString()
                  : <span className="meta">nunca</span>}
                {data.account.sapEntriesLastError && (
                  <>
                    {"  ·  "}
                    <span className="error">{data.account.sapEntriesLastError}</span>
                  </>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {syncMessage && (
        <p className={syncMessage.startsWith("❌") ? "error" : "meta"}>{syncMessage}</p>
      )}

      <div className="form-row" style={{ flexWrap: "wrap" }}>
        <label>
          Desde{" "}
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          Hasta{" "}
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <input
          placeholder="Buscar en memo / referencias / contrapartida"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyFilters();
          }}
          style={{ minWidth: "20rem" }}
        />
        <button type="button" onClick={applyFilters} disabled={loading}>
          Aplicar
        </button>
        <button type="button" onClick={clearFilters} disabled={loading}>
          Limpiar
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {!error && data === null && !loading && <p>Cargando…</p>}
      {data && data.items.length === 0 && (
        <p className="meta">
          No hay apuntes SAP persistidos para esta cuenta.
          {" "}
          {data.account.sapGLAccount
            ? "Prueba a lanzar 'Sincronizar con SAP' o comprueba el rango de fechas."
            : "El primer sync descubrirá la cuenta contable a partir del IBAN."}
        </p>
      )}
      {data && data.items.length > 0 && (
        <>
          <p className="meta">
            {data.total} apunte{data.total === 1 ? "" : "s"} en total.
          </p>
          <table className="data">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Asiento</th>
                <th>Contrapartida</th>
                <th>Memo / Referencia</th>
                <th className="num">Importe</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((e) => (
                <SapEntryRow key={`${e.jdtNum}#${e.lineId}`} entry={e} currency={currency} />
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function SapEntryRow({ entry, currency }: { entry: SapEntry; currency: string }) {
  const amountClass = entry.amount < 0 ? "num neg" : "num";
  const memo = entry.lineMemo || entry.headerMemo;
  const ref = [entry.reference1, entry.reference2].filter(Boolean).join(" · ");
  return (
    <tr>
      <td>{entry.referenceDate || <span className="meta">—</span>}</td>
      <td>
        <code>{entry.jdtNum}</code>
        <span className="meta"> · L{entry.lineId}</span>
      </td>
      <td>
        {entry.contraAccount ? <code>{entry.contraAccount}</code> : <span className="meta">—</span>}
      </td>
      <td>
        <div style={{ fontSize: "0.9em", lineHeight: 1.3 }}>
          {memo && <div>{memo}</div>}
          {ref && <div className="meta">{ref}</div>}
          {entry.transactionCode && (
            <div className="meta">TC: {entry.transactionCode}</div>
          )}
        </div>
      </td>
      <td className={amountClass}>
        {formatMoney(entry.amount, entry.currency || currency)}
      </td>
    </tr>
  );
}
